import React from 'react';
import { isEmpty, orderBy } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CategoryFilter from '~/components/core/Molecules/CategoryFilter/CategoryFilter';
import Autocomplete from '~/components/core/Molecules/Fields/AutoComplete';
import MultiSelectAutoComplete from '~/components/core/Molecules/Fields/MultiSelectAutoComplete';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import CloseIcon from '~/components/icons/CloseIcon';
import useOrganization from '~/components/OrganizationContext';
import type { Category, ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { COMMUNICATION_CHANNEL_DICT } from '~/Types';
import { displayCommunicationCategories } from '~/Utils';

import ContactSearchContainerOutOfClaim from '../ContactSearch/ContactSearchContainerOutOfClaim';
import ExposuresLabelFilter from '../exposures/ExposuresLabelsFilter';

import type { ChannelTabType, CommunicationChannelKey, HandledStatusFilter } from './types';

interface CommunicationAdjuster {
  id: string;
  username: string;
}

interface CommunicationFiltersProps {
  selectedContact: ContactFullModel | null;
  onSelectContact: (contact: ContactFullModel | null) => void;
  selectedAdjuster: CommunicationAdjuster | null;
  onSelectAdjuster: (adjuster: CommunicationAdjuster | null) => void;
  communicationAdjusters: CommunicationAdjuster[];
  filteredExposuresIds: number[];
  onUpdateFilteredExposures: (exposuresIds: number[]) => void;
  onClearFilters: () => void;
  organizationContactRolesDict: Record<string, unknown>;
  getAllSearchableContactRoles: (rolesDict: Record<string, unknown>) => string[];
  isLoading: boolean;
  activeChannelType: ChannelTabType;
  availableChannels: ChannelTabType[];
  selectedChannelsFilter: CommunicationChannelKey[] | null;
  onChangeSelectedChannelsFilter: (channels: CommunicationChannelKey[] | null) => void;
  displayAdjusterFilter?: boolean;
  displayExposuresFilter?: boolean;
  searchText?: string;
  onSearchTextChange?: (searchText: string) => void;
  onClearText?: () => void;
  selectedCategories?: number[];
  onChangeSelectedCategories?: (categoryIds: number[]) => void;
  isHandledStatusFilterAvailable?: boolean;
  handledStatusFilter?: HandledStatusFilter;
  onChangeHandledStatusFilter?: (value: HandledStatusFilter) => void;
  isHandledStatusDefaultState?: boolean;
}

const CommunicationFilters: React.FC<CommunicationFiltersProps> = ({
  selectedContact,
  onSelectContact,
  selectedAdjuster,
  onSelectAdjuster,
  communicationAdjusters,
  filteredExposuresIds,
  onUpdateFilteredExposures,
  onClearFilters,
  organizationContactRolesDict,
  getAllSearchableContactRoles,
  isLoading,
  activeChannelType,
  availableChannels,
  selectedChannelsFilter,
  onChangeSelectedChannelsFilter,
  displayAdjusterFilter = true,
  displayExposuresFilter = true,
  searchText = '',
  onSearchTextChange,
  onClearText,
  selectedCategories = [],
  onChangeSelectedCategories,
  isHandledStatusFilterAvailable,
  handledStatusFilter,
  onChangeHandledStatusFilter,
  isHandledStatusDefaultState,
}) => {
  const { communicationCategories, isLoadingCommunicationCategories, isErrorCommunicationCategories } =
    useOrganization() as {
      communicationCategories: Category[];
      isLoadingCommunicationCategories: boolean;
      isErrorCommunicationCategories: boolean;
    };

  const shouldShowCategoryFilter = displayCommunicationCategories(communicationCategories);

  const isAllTab = activeChannelType === 'all';

  const isNonAllChannel = (ch: ChannelTabType): ch is CommunicationChannelKey => ch !== 'all';
  const channelOptions: CommunicationChannelKey[] = (availableChannels || []).filter(isNonAllChannel);
  const isClearDisabled =
    !selectedContact &&
    (displayAdjusterFilter ? !selectedAdjuster : true) &&
    !(selectedChannelsFilter && selectedChannelsFilter.length > 0) &&
    !searchText &&
    (isHandledStatusDefaultState ?? true) &&
    (!shouldShowCategoryFilter || isEmpty(selectedCategories));

  const handleClearSearch = () => {
    onClearFilters();
    onSearchTextChange?.('');
    onClearText?.();
    onChangeSelectedChannelsFilter(null);
    onChangeSelectedCategories?.([]);
  };

  return (
    <div className="flex flex-col gap-24 bg-white pl-20 pt-40">
      <div>
        <div>
          <div className="flex flex-wrap items-end gap-x-12">
            {isAllTab && (
              <div>
                <MultiSelectAutoComplete<CommunicationChannelKey>
                  id="channel"
                  label="Filter by Type"
                  options={channelOptions}
                  value={selectedChannelsFilter ?? []}
                  getOptionLabel={(option) => COMMUNICATION_CHANNEL_DICT[option]}
                  onChange={(newVals?: CommunicationChannelKey[] | null) => {
                    onChangeSelectedChannelsFilter(newVals ?? null);
                  }}
                  className="w-[200px]"
                  limitTags={1}
                />
              </div>
            )}
            {isHandledStatusFilterAvailable && (
              <div>
                <SingleSelectField<HandledStatusFilter>
                  id="handled_status"
                  label="Filter by Status"
                  value={handledStatusFilter ?? 'all'}
                  onChange={(newValue) => onChangeHandledStatusFilter?.(newValue)}
                  disabled={isLoading}
                  className="w-[200px]"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="handled">Handled</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </SingleSelectField>
              </div>
            )}
            <div>
              <TextField
                id="free_text_search"
                label="Free Text Search"
                value={searchText}
                fullWidth
                onChange={(newValue) => {
                  onSearchTextChange?.(newValue as string);
                }}
                InputProps={{
                  endAdornment: searchText ? (
                    <InputAdornment position="end">
                      <IconButton className="p-8" onClick={onClearText}>
                        <CloseIcon size={10} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                className="w-[200px]"
              />
            </div>

            <ContactSearchContainerOutOfClaim
              textInputRef={null}
              overrideOfContactsContextSearchOrganizationId={null}
              disallowNew
              fixedSearchResults
              onSelectContact={onSelectContact}
              selectedContactId={
                selectedContact && typeof selectedContact === 'object' ? selectedContact.id : undefined
              }
              selectedContactDisplayName={
                selectedContact && typeof selectedContact === 'object' ? selectedContact.full_name : undefined
              }
              TextFieldProps={{
                label: 'Filter by Contact',
              }}
              acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict).concat(['user'])}
              disabled={isLoading}
              includeSingleClaimContactsInServerQuery
              reuseExistingContactsContextIfInClaim
            />

            {displayAdjusterFilter && (
              <div>
                <Autocomplete
                  id="adjuster"
                  label="Filter by Adjuster"
                  autoSelectSingleOption={false}
                  options={orderBy(communicationAdjusters, ['username'])}
                  getOptionLabel={(option: CommunicationAdjuster) => option.username}
                  onChange={(_, newVal: CommunicationAdjuster | null | undefined) => {
                    onSelectAdjuster(newVal || null);
                  }}
                  value={selectedAdjuster || { id: '', username: '' }}
                  className="w-[200px]"
                  textFieldProps={{ className: 'my-0' }}
                />
              </div>
            )}
            {shouldShowCategoryFilter && (
              <div>
                <CategoryFilter<Category>
                  categories={communicationCategories.filter((cat) => !cat.is_disabled)}
                  selectedCategories={selectedCategories}
                  onChange={onChangeSelectedCategories || (() => {})}
                  disabled={isLoading}
                  isLoading={isLoadingCommunicationCategories}
                  isError={isErrorCommunicationCategories}
                  className="w-[200px]"
                />
              </div>
            )}
            <Button disabled={isClearDisabled} onClick={handleClearSearch}>
              Clear
            </Button>
          </div>
        </div>
      </div>
      {displayExposuresFilter && (
        <div className="flex flex-row flex-wrap items-center justify-start">
          <ExposuresLabelFilter filterList={filteredExposuresIds} onUpdateFiltered={onUpdateFilteredExposures} />
        </div>
      )}
    </div>
  );
};

export default CommunicationFilters;
